import { el } from "../util/dom";
import { formatBtc, formatLocalTime, formatPrice, formatVolumeUsd } from "../util/format";
import { shareSnapshot } from "../share";
import type { ShareSnapshot } from "../share";
import { formatDisplayRatio, isNormalized, setNormalized } from "../displayMode";
import { isEntitled, LOCKED_TEXT } from "../api/entitlement";
import type { Coinbase24hStatsResponse, DailyExtremesResponse, Tick } from "../api/types";
import type { StreamStatus } from "../api/stream";

// ADR-0013 locked-slot rendering: the value span keeps its exact position;
// its content becomes the literal "Subscribers Only" in the dimmed
// `.locked-value` style (styles.css) -- never a fake/zero value, never the
// live green/red styling.
export function setLockedValue(node: HTMLElement): void {
  node.textContent = LOCKED_TEXT;
  node.classList.add("locked-value");
}

export function setLiveValue(node: HTMLElement, text: string): void {
  node.textContent = text;
  node.classList.remove("locked-value");
}

/** Stream status dot text/classes -- shared with the admin research readout
 * (bandReadoutView.ts) so both dots read identically. */
export function renderStreamStatus(statusEl: HTMLElement, status: StreamStatus): void {
  const label = status === "open" ? "Live" : status === "connecting" ? "Connecting..." : "Reconnecting...";
  statusEl.textContent = `● ${label}`;
  statusEl.className = "status" + (status === "open" ? " connected" : status === "connecting" ? "" : " error");
}

/** SW-5: a live tick may only WIDEN a displayed extreme, never shrink or
 * invent one -- null (locked slot, or no server snapshot yet) stays null,
 * per SPEC.md these are server-computed values and the periodic poll owns
 * the rolling-window expiry; this clamp only owns instant new records. */
export function clampExtreme(
  displayed: number | null,
  live: number | null | undefined,
  kind: "high" | "low"
): number | null {
  if (displayed === null || live === null || live === undefined || !Number.isFinite(live)) {
    return displayed;
  }
  return kind === "high" ? Math.max(displayed, live) : Math.min(displayed, live);
}

export interface DashboardView {
  root: HTMLElement;
  updateTick(tick: Tick): void;
  updateDailyExtremes(extremes: DailyExtremesResponse): void;
  update24hStats(stats: Coinbase24hStatsResponse): void;
  updateVolumeChange(percentChange: number | null): void;
  setStreamStatus(status: "connecting" | "open" | "error" | "closed"): void;
  /** Re-render the ratio readouts from cached data (e.g. after a scale switch). */
  rerender(): void;
  /** Register a callback fired when the user switches the raw/Normalized scale. */
  onScaleChange(cb: () => void): void;
  /** XDIV 0b: the readout exactly as displayed, for the share image. */
  snapshot(): ShareSnapshot;
  /** SHARE pill; main.ts seats it in the nav row next to MENU (Owner 2026-09-11). */
  shareButton: HTMLButtonElement;
  /** SW-31 walkthrough anchors: the element each card outlines (main.ts adds MENU to `status`). */
  fields: Record<"ratio" | "hod" | "lod" | "btc" | "depth" | "stats" | "scale" | "status", HTMLElement>;
}

export function buildDashboardView(): DashboardView {
  const statusEl = el("div", { class: "status", id: "statusIndicator" }, ["● Connecting..."]);

  const hodRatio = el("span", {}, ["--.--"]);
  const hodPrice = el("span", {}, ["[$--,---]"]);
  const mainRatio = el("div", { class: "ratio-main" }, ["--.--"]);
  const lodRatio = el("span", {}, ["--.--"]);
  const lodPrice = el("span", {}, ["[$--,---]"]);

  // Current BTC (mid) price -- always live/free like the main ratio and like
  // the Android lock screen (SPEC.md §4: ...R-LOD -> BTC price -> depths...).
  const btcPrice = el("div", { class: "btc-price-value" }, ["$--,---"]);

  const bidDepth = el("span", { class: "depth-value" }, ["--,---.-- BTC"]);
  const askDepth = el("span", { class: "depth-value" }, ["--,---.-- BTC"]);

  const volume = el("span", { class: "stat-value volume-stat" }, ["$---,---,---"]);
  const volumeChange = el("span", { class: "volume-change", id: "volumeChange" }, ["--%"]);
  const priceHigh = el("span", { class: "stat-value high-stat" }, ["$---,---"]);
  const priceLow = el("span", { class: "stat-value low-stat" }, ["$---,---"]);
  const statsAge = el("div", { class: "stats-age" }, [""]);

  // --- Raw <-> Normalized scale toggle (persisted; see displayMode.ts). The
  // ratio readouts are cached so a switch re-renders instantly without waiting
  // for the next tick / poll; main.ts also refreshes the chart on change.
  let onScaleChangeCb: (() => void) | null = null;
  let lastTick: Tick | null = null;
  let lastExtremes: DailyExtremesResponse | null = null;
  let lastStats: Coinbase24hStatsResponse | null = null;

  const rawBtn = el("button", { class: "scale-btn", type: "button" }, ["RAW"]);
  const normBtn = el("button", { class: "scale-btn", type: "button" }, ["NORMALIZED"]);
  const scaleToggle = el("div", { class: "scale-toggle" }, [
    el("span", { class: "scale-toggle-label" }, ["SCALE"]),
    rawBtn,
    normBtn,
  ]);
  function reflectScale(): void {
    const n = isNormalized();
    rawBtn.classList.toggle("active", !n);
    normBtn.classList.toggle("active", n);
  }
  function selectScale(normalized: boolean): void {
    if (isNormalized() === normalized) return;
    setNormalized(normalized);
    reflectScale();
    renderRatios();
    onScaleChangeCb?.();
  }
  rawBtn.addEventListener("click", () => selectScale(false));

  // XDIV 0b share affordance: renders the readout as displayed into a
  // watermarked, UTC-timestamped image and hands it to the OS share sheet
  // (share.ts). Disabled until the first tick so an empty readout is never
  // shared.
  const shareBtn = el("button", { class: "nav-btn share-btn", type: "button", disabled: "" }, ["SHARE"]);
  function snapshot(): ShareSnapshot {
    return {
      mainRatio: mainRatio.textContent ?? "",
      hodRatio: hodRatio.textContent ?? "",
      hodPrice: hodPrice.textContent ?? "",
      lodRatio: lodRatio.textContent ?? "",
      lodPrice: lodPrice.textContent ?? "",
      btcPrice: btcPrice.textContent ?? "",
      bidDepth: bidDepth.textContent ?? "",
      askDepth: askDepth.textContent ?? "",
      scale: isNormalized() ? "NORMALIZED" : "RAW",
      bandWidthUsd: lastTick?.band_width_usd ?? null,
    };
  }
  shareBtn.addEventListener("click", () => {
    shareBtn.disabled = true;
    void shareSnapshot(snapshot())
      .catch(() => undefined)
      .finally(() => {
        shareBtn.disabled = false;
      });
  });
  normBtn.addEventListener("click", () => selectScale(true));
  reflectScale();

  function renderTick(tick: Tick): void {
    lastTick = tick;
    shareBtn.disabled = false;
    // The main ratio is free-tier (ADR-0013) -- always live.
    mainRatio.textContent = formatDisplayRatio(tick.ratio);
    // Current BTC price is free/public too (never redacted) -- always live.
    btcPrice.textContent = formatPrice(tick.mid_price);
    if (isEntitled(tick)) {
      setLiveValue(bidDepth, formatBtc(tick.bid_depth));
      setLiveValue(askDepth, formatBtc(tick.ask_depth));
    } else {
      // ADR-0013: bid/ask depth are paywalled; the server nulls them.
      setLockedValue(bidDepth);
      setLockedValue(askDepth);
    }
  }
  function renderExtremes(extremes: DailyExtremesResponse): void {
    lastExtremes = extremes;
    if (isEntitled(extremes)) {
      setLiveValue(hodRatio, formatDisplayRatio(extremes.hod_ratio));
      hodPrice.textContent = extremes.hod_mid_price !== null ? `[${formatPrice(extremes.hod_mid_price)}]` : "[$--,---]";
      setLiveValue(lodRatio, formatDisplayRatio(extremes.lod_ratio));
      lodPrice.textContent = extremes.lod_mid_price !== null ? `[${formatPrice(extremes.lod_mid_price)}]` : "[$--,---]";
    } else {
      // ADR-0013: R-HOD/R-LOD are paywalled -- locked text in the ratio slot,
      // blank price line (a space preserves the fixed layout's height).
      setLockedValue(hodRatio);
      hodPrice.textContent = " ";
      setLockedValue(lodRatio);
      lodPrice.textContent = " ";
    }
  }
  function renderStats(stats: Coinbase24hStatsResponse): void {
    lastStats = stats;
    volume.textContent = formatVolumeUsd(stats.volume_24h_usd);
    priceHigh.textContent = formatPrice(stats.price_24h_high);
    priceLow.textContent = formatPrice(stats.price_24h_low);
    statsAge.textContent = stats.fetched_at_utc
      ? `24h stats as of ${formatLocalTime(stats.fetched_at_utc)}`
      : "";
  }
  function renderRatios(): void {
    if (lastTick) renderTick(lastTick);
    if (lastExtremes) renderExtremes(lastExtremes);
  }

  // R-HOD/R-LOD stacked to mirror the Android ClockModeScreen exactly
  // (Owner 2026-08-20): R-HOD = label above value above bracketed price;
  // R-LOD mirrored with the label underneath; no extreme timestamps.
  const hodGroup = el("div", { class: "hod-ratio" }, [
    el("div", { class: "extreme-label" }, ["R-HOD"]),
    el("div", { class: "extreme-value" }, [hodRatio]),
    el("div", { class: "extreme-price" }, [hodPrice]),
  ]);
  const lodGroup = el("div", { class: "lod-ratio" }, [
    el("div", { class: "extreme-value" }, [lodRatio]),
    el("div", { class: "extreme-price" }, [lodPrice]),
    el("div", { class: "extreme-label" }, ["R-LOD"]),
  ]);
  const btcSection = el("div", { class: "btc-price-section" }, [
    el("div", { class: "btc-price-label" }, ["BITCOIN"]),
    btcPrice,
  ]);
  const depthSection = el("div", { class: "depth-section" }, [
    el("div", { class: "bid-depth" }, [el("span", { class: "depth-label" }, ["Bid Depth:"]), bidDepth]),
    el("div", { class: "ask-depth" }, [el("span", { class: "depth-label" }, ["Ask Depth:"]), askDepth]),
  ]);
  const statsSection = el("div", { class: "consolidated-stats" }, [
    el("div", { class: "stat-item volume-column" }, [
      el("div", {}, [volume, volumeChange]),
      el("span", { class: "stat-label" }, ["24H VOL"]),
    ]),
    el("div", { class: "stat-item price-column" }, [
      el("div", { class: "price-row" }, [el("span", { class: "stat-label" }, ["HIGH:"]), priceHigh]),
      el("div", { class: "price-row" }, [el("span", { class: "stat-label" }, ["LOW:"]), priceLow]),
    ]),
  ]);

  const root = el("div", { class: "dashboard-core" }, [
    el("div", { class: "header" }, [el("div", { class: "title" }, ["BitClock Order Book Ratio"]), statusEl]),
    scaleToggle,
    el("div", { class: "ratio-section" }, [hodGroup, mainRatio, lodGroup]),
    btcSection,
    depthSection,
    statsSection,
    statsAge,
  ]);

  return {
    root,
    shareButton: shareBtn,
    fields: {
      ratio: mainRatio,
      hod: hodGroup,
      lod: lodGroup,
      btc: btcSection,
      depth: depthSection,
      stats: statsSection,
      scale: scaleToggle,
      status: statusEl,
    },
    updateTick(tick: Tick): void {
      renderTick(tick);
      // SW-5: a new extreme must show in the stat readouts on the very tick
      // that printed it, not on the next 10s/30s poll. Clamp the cached
      // snapshots (renderStats/renderExtremes re-cache the clamped copy, so
      // it also survives scale-switch rerenders until the next poll).
      if (lastStats) {
        const high = clampExtreme(lastStats.price_24h_high, tick.mid_price, "high");
        const low = clampExtreme(lastStats.price_24h_low, tick.mid_price, "low");
        if (high !== lastStats.price_24h_high || low !== lastStats.price_24h_low) {
          renderStats({ ...lastStats, price_24h_high: high, price_24h_low: low });
        }
      }
      if (lastExtremes) {
        // T-156 per-second true extremes when present, else the tick ratio.
        const hod = clampExtreme(lastExtremes.hod_ratio, tick.ratio_window_max ?? tick.ratio, "high");
        const lod = clampExtreme(lastExtremes.lod_ratio, tick.ratio_window_min ?? tick.ratio, "low");
        if (hod !== lastExtremes.hod_ratio || lod !== lastExtremes.lod_ratio) {
          renderExtremes({
            ...lastExtremes,
            hod_ratio: hod,
            hod_mid_price: hod !== lastExtremes.hod_ratio ? tick.mid_price : lastExtremes.hod_mid_price,
            hod_ts_utc: hod !== lastExtremes.hod_ratio ? tick.ts_utc : lastExtremes.hod_ts_utc,
            lod_ratio: lod,
            lod_mid_price: lod !== lastExtremes.lod_ratio ? tick.mid_price : lastExtremes.lod_mid_price,
            lod_ts_utc: lod !== lastExtremes.lod_ratio ? tick.ts_utc : lastExtremes.lod_ts_utc,
          });
        }
      }
    },
    updateDailyExtremes(extremes: DailyExtremesResponse): void {
      renderExtremes(extremes);
    },
    update24hStats(stats: Coinbase24hStatsResponse): void {
      renderStats(stats);
    },
    updateVolumeChange(percentChange: number | null): void {
      if (percentChange === null) {
        volumeChange.textContent = "--%";
        volumeChange.className = "volume-change";
        return;
      }
      volumeChange.textContent = `${percentChange > 0 ? "+" : ""}${percentChange.toFixed(1)}%`;
      volumeChange.className = "volume-change " + (percentChange > 0 ? "positive" : "negative");
    },
    setStreamStatus(status): void {
      renderStreamStatus(statusEl, status);
    },
    rerender(): void {
      renderRatios();
    },
    onScaleChange(cb: () => void): void {
      onScaleChangeCb = cb;
    },
    snapshot,
  };
}
